import { useEffect, useMemo } from "react"
import { COMPOSITION_METRIC_IDS } from "helpers/metrics/constants"
import { shouldHideMarketValueColumn } from "helpers/metrics/utils"

export type SourceMetricIdsChangeHandler = (metricIds: string[] | null) => void

export interface CompositionVisibilityOptions {
  instrumentColumns?: string[]
  originalInstrument?: { type?: string } | null
}

// Stable composition-visibility object shared by every template that sources composition
// columns, so the four call sites can't drift on shape or memoization deps.
export function useCompositionVisibility(
  instrumentColumns: string[],
  originalInstrument: CompositionVisibilityOptions["originalInstrument"],
): CompositionVisibilityOptions {
  return useMemo(
    () => ({ instrumentColumns, originalInstrument }),
    [instrumentColumns, originalInstrument],
  )
}

export function getPrependableCompositionMetricIds(
  flattenedMetrics: Record<string, unknown>,
  visibility: CompositionVisibilityOptions = {},
) {
  return COMPOSITION_METRIC_IDS.filter((metricId) => {
    if (!flattenedMetrics[metricId]) {
      return false
    }

    if (metricId === "marketValue" && shouldHideMarketValueColumn(visibility.instrumentColumns, visibility.originalInstrument)) {
      return false
    }

    return true
  })
}

export function withDefaultCompositionMetricIds(
  metricIds: string[] | null | undefined,
  flattenedMetrics: Record<string, unknown>,
  visibility: CompositionVisibilityOptions = {},
) {
  // Built-in views render composition columns even when they have no view-specific
  // metrics (e.g. a beta view with no beta fields), so prepend them for empty arrays too.
  // A null id list means "not sourced yet" and must stay null.
  if (metricIds == null) {
    return null
  }

  const compositionMetricIds = getPrependableCompositionMetricIds(flattenedMetrics, visibility)
  const nonCompositionMetricIds = metricIds.filter((metricId) => !COMPOSITION_METRIC_IDS.includes(metricId))

  return [...compositionMetricIds, ...nonCompositionMetricIds]
}

interface UseSourceDisplayedMetricIdsParams {
  metricIds: string[] | null | undefined
  flattenedMetrics?: Record<string, unknown> | null
  onMetricsChange?: SourceMetricIdsChangeHandler
  includeDefaultCompositionMetrics?: boolean
  compositionVisibility?: CompositionVisibilityOptions
}

const EMPTY_COMPOSITION_VISIBILITY: CompositionVisibilityOptions = {}

export function useSourceDisplayedMetricIds({
  metricIds,
  flattenedMetrics,
  onMetricsChange,
  includeDefaultCompositionMetrics = false,
  compositionVisibility = EMPTY_COMPOSITION_VISIBILITY,
}: UseSourceDisplayedMetricIdsParams) {
  useEffect(() => {
    if (!onMetricsChange || !flattenedMetrics) {
      return
    }

    const displayedMetricIds = includeDefaultCompositionMetrics
      ? withDefaultCompositionMetricIds(metricIds, flattenedMetrics, compositionVisibility)
      : metricIds ?? null

    onMetricsChange(displayedMetricIds)
    return () => onMetricsChange(null)
  }, [metricIds, flattenedMetrics, onMetricsChange, includeDefaultCompositionMetrics, compositionVisibility])
}
